import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Youtube, Instagram } from 'lucide-react';

const Footer = ({ language }) => {
  return (
    <footer className="w-full">
      {/* Related Portals Bar */}
      <div style={{ backgroundColor: '#cc0000' }} className="text-white py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-6 text-xs font-medium">
            <span className="opacity-80">{language === 'bm' ? 'Portal Berkaitan:' : 'Related Portals:'}</span>
            {['Portal Rasmi JPJ', 'JPJeBid', 'Orang Awam', 'Rakan Strategik', 'PKJR', 'Warga JPJ'].map((portal) => (
              <a
                key={portal}
                href="#"
                className="hover:text-yellow-200 transition-colors"
              >
                {portal}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div style={{ backgroundColor: '#00264d' }} className="text-white py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* JPJ Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Jabatan_Pengangkutan_Jalan.svg/120px-Jabatan_Pengangkutan_Jalan.svg.png"
                  alt="Logo JPJ Malaysia"
                  style={{ width: 52, height: 54, objectFit: 'contain' }}
                />
                <div>
                  <div className="font-bold text-sm">JABATAN PENGANGKUTAN JALAN</div>
                  <div className="text-xs opacity-70">ROAD TRANSPORT DEPARTMENT MALAYSIA</div>
                </div>
              </div>
              <div className="text-xs opacity-70 space-y-1 mt-3">
                <p className="font-semibold opacity-90 text-sm mb-2">{language === 'bm' ? 'Ibu Pejabat' : 'Headquarters'}</p>
                <div className="flex gap-2">
                  <MapPin size={14} className="mt-0.5 flex-shrink-0 opacity-60" />
                  <p>Aras 3-5, No. 26, Jalan Tun Hussein, Presint 4,<br />Pusat Pentadbiran Kerajaan Persekutuan,<br />62100 W.P. Putrajaya.</p>
                </div>
                <div className="flex gap-2 mt-2">
                  <Phone size={14} className="mt-0.5 flex-shrink-0 opacity-60" />
                  <div>
                    <p>+603 8000 8000 <span className="opacity-60">(Pusat Panggilan)</span></p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Mail size={14} className="mt-0.5 flex-shrink-0 opacity-60" />
                  <p>binajpj@jpj.gov.my</p>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold text-sm mb-4 text-yellow-300">
                {language === 'bm' ? 'Pautan Pantas' : 'Quick Links'}
              </h4>
              <ul className="space-y-2 text-xs opacity-80">
                {[
                  { label: language === 'bm' ? 'Laman Utama' : 'Home', path: '/' },
                  { label: language === 'bm' ? 'Semak Lesen Memandu' : 'Check Driving License', path: '/semak-lesen' },
                  { label: language === 'bm' ? 'Semak Saman' : 'Check Summons', path: '/semak-saman' },
                  { label: language === 'bm' ? 'Pembaharuan Lesen' : 'License Renewal', path: '/pembaharuan' },
                  { label: language === 'bm' ? 'Cari Cawangan' : 'Find Branch', path: '/cawangan' },
                  { label: language === 'bm' ? 'Soalan Lazim' : 'FAQ', path: '/faq' },
                  { label: language === 'bm' ? 'Hubungi Kami' : 'Contact Us', path: '/hubungi' },
                ].map((link, i) => (
                  <li key={i}>
                    <Link to={link.path} className="hover:text-yellow-300 transition-colors">
                      &rsaquo; {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social Media */}
            <div>
              <h4 className="font-bold text-sm mb-4 text-yellow-300">
                {language === 'bm' ? 'Media Sosial' : 'Social Media'}
              </h4>
              <div className="space-y-3">
                {[
                  { icon: Facebook, label: 'Facebook', handle: '@jabatanpengangkutanjalanmalaysia' },
                  { icon: Youtube, label: 'YouTube', handle: '@portaljpj' },
                  { icon: Instagram, label: 'Instagram', handle: '@jpjmalaysia' },
                ].map((social, i) => (
                  <a key={i} href="#" className="flex items-center gap-2 text-xs opacity-80 hover:opacity-100 hover:text-yellow-300 transition-all">
                    <social.icon size={16} />
                    <div>
                      <div className="font-semibold">{social.label}</div>
                      <div className="opacity-60 text-xs">{social.handle}</div>
                    </div>
                  </a>
                ))}
              </div>

              <div className="mt-6">
                <h4 className="font-bold text-xs mb-2 text-yellow-300 uppercase tracking-wide">
                  {language === 'bm' ? 'Aplikasi Rasmi' : 'Official App'}
                </h4>
                <div className="inline-flex items-center gap-2 bg-white/10 rounded px-3 py-2 text-xs hover:bg-white/20 transition-colors cursor-pointer">
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: '#003580' }}>
                    <div className="w-full h-full flex items-center justify-center text-white font-black" style={{ fontSize: '8px' }}>MY</div>
                  </div>
                  <div>
                    <div className="font-bold">MyJPJ</div>
                    <div className="opacity-60" style={{ fontSize: '9px' }}>iOS & Android</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div style={{ backgroundColor: '#001a36' }} className="text-white py-3">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-2 text-xs opacity-60">
          <p>Copyright &copy; 2025 Jabatan Pengangkutan Jalan Malaysia. Hak Cipta Terpelihara.</p>
          <div className="flex gap-4">
            <a href="#" className="hover:opacity-100 transition-opacity">{language === 'bm' ? 'Dasar Keselamatan' : 'Security Policy'}</a>
            <span>|</span>
            <a href="#" className="hover:opacity-100 transition-opacity">{language === 'bm' ? 'Dasar Privasi' : 'Privacy Policy'}</a>
            <span>|</span>
            <a href="#" className="hover:opacity-100 transition-opacity">{language === 'bm' ? 'Penafian' : 'Disclaimer'}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
